import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import type { KeyAreaSummary } from './types';

/** Canonical tab order and labels */
const KEY_AREA_ORDER = [
  'RISK MONITORING',
  'RISK MITIGATION',
  'CAPACITY DEVELOPMENT',
  'TOOLs and RESOURCES',
  'GLOBAL FMD CONTROL',
  'FAST CONTROL',
  'VACCINE SECURITY',
];

function keyAreaSortIndex(ka: KeyAreaSummary): number {
  const raw = (ka.short_label || ka.name || '').replace(/^\[|\]$/g, '').trim();
  const upper = raw.toUpperCase();
  const idx = KEY_AREA_ORDER.findIndex((l) => l.toUpperCase() === upper);
  if (idx >= 0) return idx;
  if (upper.includes('TOOL') && upper.includes('RESOURCE')) return 3;
  return 100 + ka.id;
}

interface KeyAreaTabsProps {
  keyAreas: KeyAreaSummary[];
  selectedKeyAreaId: number | null;
  focusFilterId: number | null;
  onSelect: (id: number | null) => void;
}

const KeyAreaTabs: React.FC<KeyAreaTabsProps> = ({
  keyAreas,
  selectedKeyAreaId,
  focusFilterId,
  onSelect,
}) => {
  const visible = useMemo(() => {
    const filtered =
      focusFilterId == null
        ? keyAreas
        : keyAreas.filter((k) => k.focus_objective_id === focusFilterId);
    return [...filtered].sort((a, b) => keyAreaSortIndex(a) - keyAreaSortIndex(b));
  }, [keyAreas, focusFilterId]);

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <motion.button
        type="button"
        layout
        onClick={() => onSelect(null)}
        className={`text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
          selectedKeyAreaId == null
            ? 'bg-[#15736d] text-white border-[#15736d]'
            : 'bg-white text-gray-700 border-gray-200 hover:border-[#15736d]/60'
        }`}
      >
        Overview
      </motion.button>
      {visible.map((ka) => {
        const active = selectedKeyAreaId === ka.id;
        const pct = ka.avg_progress_pct;
        return (
          <motion.button
            key={ka.id}
            type="button"
            layout
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => onSelect(active ? null : ka.id)}
            className={`group text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-lg border transition-all flex items-center gap-2 ${
              active
                ? 'bg-[#0f5b57] text-white border-[#0f5b57] shadow-sm'
                : 'bg-white text-gray-700 border-gray-200 hover:border-[#15736d]/60'
            }`}
          >
            <span>{ka.short_label}</span>
            {pct != null && (
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                  active ? 'bg-white/20 text-white' : 'bg-[#e8f3f1] text-[#15736d]'
                } ${active ? 'animate-pulse' : ''}`}
              >
                {pct}%
              </span>
            )}
          </motion.button>
        );
      })}
    </div>
  );
};

export default KeyAreaTabs;
